import CodeBlock from './CodeBlock';
import MermaidDiagram from './MermaidDiagram';
import ErrorBoundary from './ErrorBoundary';
import { isDamyxActionBlock } from '../utils/damyxFence';
import { useLanguage } from '../context/LanguageContext';

// MERMAID_TYPES — solo se reconoce 'mermaid' explícito (FASE 13.2 fix).
// Mantener la lista comentada por referencia histórica; el resto de tipos
// se filtraban heurísticamente y causaban falsos positivos.
// const MERMAID_TYPES = ['mermaid', 'flowchart', 'sequenceDiagram', 'classDiagram', 'stateDiagram', 'erDiagram', 'gantt', 'pie', 'gitGraph', 'mindmap', 'timeline'];

const MessageContent = ({ content }) => {
  const { language } = useLanguage();

  const diagramTitles = {
    es: {
      'flowchart': 'Diagrama de Flujo',
      'sequencediagram': 'Diagrama de Secuencia',
      'classdiagram': 'Diagrama de Clases',
      'statediagram': 'Diagrama de Estados',
      'erdiagram': 'Diagrama ER',
      'gantt': 'Diagrama de Gantt',
      'pie': 'Gráfico Circular',
      'gitgraph': 'Git Graph',
      'mindmap': 'Mapa Mental',
      'timeline': 'Línea de Tiempo',
      'mermaid': 'Diagrama',
      'default': 'Diagrama'
    },
    en: {
      'flowchart': 'Flowchart',
      'sequencediagram': 'Sequence Diagram',
      'classdiagram': 'Class Diagram',
      'statediagram': 'State Diagram',
      'erdiagram': 'ER Diagram',
      'gantt': 'Gantt Chart',
      'pie': 'Pie Chart',
      'gitgraph': 'Git Graph',
      'mindmap': 'Mind Map',
      'timeline': 'Timeline',
      'mermaid': 'Diagram',
      'default': 'Diagram'
    }
  };

  // Check if language is a mermaid diagram type.
  // FASE 13.2 fix: solo renderizamos Mermaid cuando el bloque tiene el
  // language tag EXACTO `mermaid` (lo que el DiagramGeneratorModal genera
  // explícitamente). Antes detectábamos también palabras como `flowchart`,
  // `sequenceDiagram`, `pie`, `timeline`, etc — eso causaba que texto normal
  // o pseudocódigo del LLM disparara "Syntax error in text — mermaid 11.x".
  // El render explícito de diagramas Mermaid (DiagramGeneratorModal) sigue
  // funcionando; solo desactivamos la detección heurística agresiva.
  const isMermaidDiagram = (lang, _code) => {
    return (lang || '').toLowerCase() === 'mermaid';
  };

  // Get diagram title from code
  const getDiagramTitle = (lang, code) => {
    const titles = diagramTitles[language] || diagramTitles.es;
    
    const firstLine = code?.split('\n')[0]?.trim()?.toLowerCase() || '';
    for (const [key, title] of Object.entries(titles)) {
      if (lang?.toLowerCase() === key || firstLine.startsWith(key)) {
        return title;
      }
    }
    return titles.default;
  };

  // Parse content and extract code blocks
  const parseContent = (text) => {
    const parts = [];
    const codeBlockRegex = /```([\w+.#-]*)\n?([\s\S]*?)```/g;
    let lastIndex = 0;
    let match;

    while ((match = codeBlockRegex.exec(text)) !== null) {
      // Add text before code block
      if (match.index > lastIndex) {
        const textBefore = text.slice(lastIndex, match.index);
        if (textBefore.trim()) {
          parts.push({ type: 'text', content: textBefore });
        }
      }

      // Add code block
      const language = match[1] || '';
      const code = match[2].trim();

      // 🟥 P26.33 · Los fences de acción de DamyxIA no se muestran ni se
      // tratan como código: son instrucciones internas para el Workspace.
      if (isDamyxActionBlock(language, code)) {
        lastIndex = match.index + match[0].length;
        continue;
      }

      // Check if it's a mermaid diagram
      if (isMermaidDiagram(language, code)) {
        parts.push({ 
          type: 'mermaid', 
          code,
          title: getDiagramTitle(language, code)
        });
      } else {
        parts.push({ type: 'code', language, code });
      }

      lastIndex = match.index + match[0].length;
    }

    // Add remaining text after last code block
    if (lastIndex < text.length) {
      const remainingText = text.slice(lastIndex);
      if (remainingText.trim()) {
        parts.push({ type: 'text', content: remainingText });
      }
    }

    // If no code blocks found, return the whole text
    if (parts.length === 0) {
      parts.push({ type: 'text', content: text });
    }

    return parts;
  };

  // Render inline images (base64)
  const renderTextWithImages = (text) => {
    // Match markdown images with base64 data
    const imageRegex = /!\[([^\]]*)\]\((data:image\/[^;]+;base64,[^)]+)\)/g;
    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = imageRegex.exec(text)) !== null) {
      // Add text before image
      if (match.index > lastIndex) {
        parts.push({ type: 'text', content: text.slice(lastIndex, match.index) });
      }
      // Add image
      parts.push({ type: 'image', alt: match[1], src: match[2] });
      lastIndex = match.index + match[0].length;
    }

    // Add remaining text
    if (lastIndex < text.length) {
      parts.push({ type: 'text', content: text.slice(lastIndex) });
    }

    if (parts.length === 0) {
      return <span className="whitespace-pre-wrap">{text}</span>;
    }

    return parts.map((part, i) => {
      if (part.type === 'image') {
        return (
          <img 
            key={i} 
            src={part.src} 
            alt={part.alt} 
            className="max-w-full h-auto rounded-lg my-2 border border-zinc-700"
          />
        );
      }
      return <span key={i} className="whitespace-pre-wrap">{part.content}</span>;
    });
  };

  const parts = parseContent(content);

  return (
    <div className="message-content">
      {parts.map((part, index) => {
        if (part.type === 'mermaid') {
          // 🟥 CUADRO NEGRO P1 — Mermaid puede lanzar "Syntax error in text"
          // sobre mensajes antiguos por incompatibilidad de mermaid v11.x.
          // Envolvemos en ErrorBoundary para que el resto del MessageBubble
          // siga renderizando aunque el diagrama falle.
          return (
            <ErrorBoundary
              key={index}
              fallback={(msg) => (
                <div
                  className="my-2 px-3 py-2 rounded-md border border-rose-500/30 bg-rose-950/20 text-xs text-rose-300 light:bg-rose-50 light:border-rose-200 light:text-rose-700"
                  data-testid="mermaid-error-fallback"
                >
                  <div className="font-medium">No se pudo renderizar el diagrama Mermaid.</div>
                  {msg && <div className="opacity-70 mt-0.5 font-mono break-all">{msg}</div>}
                </div>
              )}
            >
              <MermaidDiagram
                code={part.code}
                title={part.title}
              />
            </ErrorBoundary>
          );
        }
        if (part.type === 'code') {
          return (
            <CodeBlock
              key={index}
              code={part.code}
              language={part.language}
            />
          );
        }
        return (
          <p key={index} className="leading-7">
            {renderTextWithImages(part.content)}
          </p>
        );
      })}
    </div>
  );
};

export default MessageContent;
